import React from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuSave } from 'react-icons/lu';

import { CreateApplicant } from './CreateApplicant';
import { CensorCoApplicant } from '~/features/leadDetails/components/Overview/CustomerData/HeaderActions/CensorCoApplicant.tsx';
import { HideCoApplicant } from '~/features/leadDetails/components/Overview/CustomerData/HeaderActions/HideCoApplicant.tsx';
import { RevealCoApplicant } from '~/features/leadDetails/components/Overview/CustomerData/HeaderActions/RevealCoApplicant.tsx';
import { SwitchApplicants } from '~/features/leadDetails/components/Overview/CustomerData/HeaderActions/SwitchApplicants.tsx';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { canSubmitLeadDetailsForms } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

export const HeaderActions: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const { hasDirtyForms, resetAll, trigger } = useLeadDetailsFormContext();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });

    const isSaveDisabled = !canSubmitLeadDetailsForms(permissions) || !hasDirtyForms;

    return (
        <div className="flex gap-2">
            {!lead.antragsteller2 && <CreateApplicant lead={lead} />}
            {lead.antragsteller2 && (
                <>
                    <SwitchApplicants lead={lead} />
                    {!lead.coApplicantHidden ? <HideCoApplicant lead={lead} /> : <RevealCoApplicant lead={lead} />}
                    <CensorCoApplicant lead={lead} />
                </>
            )}
            <Button
                onClick={() => {
                    resetAll(lead);
                }}
                eventStopPropagation
                variant={ButtonVariant.SECONDARY}
                disabled={!hasDirtyForms}
                iconLeft={<LuCircleX size={14} />}
            >
                <span>{t('BUTTONS.DISCARD_CHANGES')}</span>
            </Button>
            <Button
                onClick={() => {
                    trigger();
                }}
                eventStopPropagation
                disabled={isSaveDisabled}
                iconLeft={<LuSave />}
            >
                <span>{t('BUTTONS.SAVE')}</span>
            </Button>
        </div>
    );
};
